import React, { useMemo } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

export type PathfinderToolGroupId = 'find' | 'analyze' | 'narrate';

/** Rail captions are first 4 letters (STAT / NARR / FIND). Charts first so they mount before search tools. */
export const PATHFINDER_TOOL_GROUPS: { id: PathfinderToolGroupId; label: string }[] = [
  { id: 'analyze', label: 'Stats' },
  { id: 'narrate', label: 'Narrate' },
  { id: 'find', label: 'Find' },
];

/** Match app-shell collapsed left nav. */
export const PATHFINDER_TOOL_RAIL_WIDTH = 48;

export interface PathfinderToolItem {
  id: string;
  label: string;
  group: PathfinderToolGroupId;
  icon: React.ReactNode;
}

export interface PathfinderToolRailProps {
  tools: PathfinderToolItem[];
  activeToolId: string;
  onSelect: (id: string) => void;
  /** Vertical icon strip (desktop) or grouped list (mobile drawer). */
  variant?: 'rail' | 'list';
}

function groupTools(tools: PathfinderToolItem[]) {
  return PATHFINDER_TOOL_GROUPS
    .map((g) => ({
      ...g,
      tools: tools.filter((t) => t.group === g.id),
    }))
    .filter((g) => g.tools.length > 0);
}

const PathfinderToolRail: React.FC<PathfinderToolRailProps> = ({
  tools,
  activeToolId,
  onSelect,
  variant = 'rail',
}) => {
  const groups = useMemo(() => groupTools(tools), [tools]);

  if (variant === 'list') {
    return (
      <Box sx={{ px: 1, pb: 2 }}>
        {groups.map((group) => (
          <Box key={group.id} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 1.5,
                py: 0.75,
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
              }}
            >
              {group.label}
            </Typography>
            <List dense disablePadding>
              {group.tools.map((tool) => {
                const selected = tool.id === activeToolId;
                return (
                  <ListItemButton
                    key={tool.id}
                    selected={selected}
                    onClick={() => onSelect(tool.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.25,
                      '&.Mui-selected': {
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.22),
                        '&:hover': {
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.3),
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: selected ? 'primary.main' : 'text.secondary' }}>
                      {tool.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={tool.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: selected ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      role="toolbar"
      aria-label="Pathfinder tools"
      sx={{
        width: PATHFINDER_TOOL_RAIL_WIDTH,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1,
        gap: 0.25,
        borderLeft: 1,
        borderColor: 'divider',
        bgcolor: (t) => alpha(t.palette.background.paper, 0.72),
        backdropFilter: 'blur(10px)',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {groups.map((group, gi) => (
        <Box
          key={group.id}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            pt: gi === 0 ? 0 : 0.75,
            mt: gi === 0 ? 0 : 0.5,
            borderTop: gi === 0 ? 0 : 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '0.55rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'text.disabled',
              lineHeight: 1,
              mb: 0.5,
              userSelect: 'none',
            }}
          >
            {group.label.slice(0, 4)}
          </Typography>
          {group.tools.map((tool) => {
            const selected = tool.id === activeToolId;
            return (
              <Tooltip key={tool.id} title={tool.label} placement="left" enterDelay={300}>
                <IconButton
                  size="small"
                  aria-label={tool.label}
                  aria-pressed={selected}
                  onClick={() => onSelect(tool.id)}
                  sx={{
                    width: 36,
                    height: 36,
                    mb: 0.25,
                    borderRadius: 1,
                    color: selected ? 'primary.contrastText' : 'text.secondary',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                    boxShadow: selected
                      ? (t) => `0 0 10px ${alpha(t.palette.primary.main, 0.45)}`
                      : 'none',
                    '&:hover': {
                      bgcolor: selected
                        ? 'primary.dark'
                        : (t) => alpha(t.palette.action.hover, 0.8),
                      color: selected ? 'primary.contrastText' : 'text.primary',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.15rem',
                    },
                  }}
                >
                  {tool.icon}
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default PathfinderToolRail;
